import * as React from "react";
import type {
  LoadPeopleOptions,
  SearchAttachableDocuments,
  UploadAttachment,
} from "@ando/ui";
import {
  buildEmailAttachment,
  searchAttachableFiles,
  searchPeople,
} from "@ando/core";

/**
 * Shared action-editor transports — single DRY anchor for the callbacks
 * that every ``<ActionDrawer>`` / ``<ActionFieldEditor>`` / preview mount
 * forwards down to ``<RecipientsInput>`` and the file picker.
 *
 * Why a dedicated module?
 *   - Inline arrow closures defined per render in the mount component
 *     (``ScalarActionProposalMount``, ``FocusedActionView``) get a fresh
 *     reference on every keystroke. ``<RecipientsInput>``'s debounce
 *     ``useEffect`` lists the callback as a dep, so the timer re-arms on
 *     every parent re-render — wasted work + extra renders downstream.
 *   - Multiple mount sites would otherwise repeat the same closure body
 *     (DRY violation flagged in the June 9, 2026 audit).
 *
 * Both hooks return ref-stable callbacks (memoised with empty deps —
 * ``searchPeople`` and ``buildEmailAttachment`` are pure module-level
 * functions so the closure has no captured state to invalidate).
 */

/**
 * Returns a stable ``loadPeopleOptions`` transport that fans out to the
 * ``GET /people/search`` endpoint via ``@ando/core``. Aborts the
 * in-flight request when React tears the effect down (component unmount
 * or dependency change inside ``<RecipientsInput>``).
 *
 * The signal is wired ALL the way through: ``RecipientsInput`` cancels
 * its debounce timer AND aborts the fetch when a fresh keystroke
 * triggers a new query, so a slow first response cannot overwrite the
 * latest user intent ("typed fast → stale list appeared after I picked
 * a match" race that auditors flagged on June 9, 2026).
 */
export function useLoadPeopleOptions(): LoadPeopleOptions {
  return React.useCallback<LoadPeopleOptions>(
    async ({ query, connectionId, signal }) => {
      return await searchPeople({ q: query, connectionId, signal });
    },
    [],
  );
}

/**
 * Returns a stable ``uploadAttachment`` transport that wraps
 * :func:`buildEmailAttachment` from ``@ando/core`` and projects the
 * returned shape onto the UI's ``AttachmentChipValue`` contract. Same
 * single helper backs every attachment surface (reply composer, HITL
 * action form, manual "Nuova email" mount) — DRY anchor:
 * ``packages/core/src/api/endpoints/emailSend.ts``.
 */
export function useUploadAttachment(): UploadAttachment {
  return React.useCallback<UploadAttachment>(async (file, opts) => {
    const built = await buildEmailAttachment(file, opts);
    return {
      filename: built.filename,
      mime_type: built.mime_type,
      size_bytes: file.size,
      content_b64: built.content_b64,
      minio_key: built.minio_key,
    };
  }, []);
}

/**
 * Returns a stable ``searchAttachableDocuments`` transport for the
 * "Scegli dai documenti Ando" attachment picker. DRY: wraps
 * :func:`searchAttachableFiles` (``@ando/core``) — the SAME
 * ``GET /actions/attachments/search`` endpoint the agent's
 * ``search_attachable_files`` tool uses — and projects the wire shape
 * (``dedup_id`` = content_item id) onto the UI's ``AttachableDocumentOption``.
 */
export function useSearchAttachableDocuments(): SearchAttachableDocuments {
  return React.useCallback<SearchAttachableDocuments>(async ({ query, signal }) => {
    const files = await searchAttachableFiles({ q: query, limit: 30, signal });
    return files.map((f) => ({
      contentItemId: f.dedup_id,
      filename: f.filename,
      mimeType: f.mime_type,
      sizeBytes: f.file_size,
      sourceLabel: f.source_label,
    }));
  }, []);
}
